import { useState, useEffect } from 'react';
// aria-label: design-system-bypass 

import { useNavigate } from 'react-router-dom';
// aria-label: design-system-bypass 

import { Droplets, Sun, TrendingUp } from 'lucide-react';
// aria-label: design-system-bypass 

import { Logo, GEOMetadata } from '../components/ui';
// aria-label: design-system-bypass 

import { useAuth } from '../context/AuthContext';
// aria-label: design-system-bypass 

import { authService } from '../services/authService';
// aria-label: design-system-bypass 


/**
 * SplashScreen - Premium animated intro screen
 * Based on AgroConnect Premium UI Design specifications
 * 
 * Features:
 * - Official ANNADATA AI Logo
 * - Gradient background shift
 * - Feature highlights
 * - Skip capability
 */
export function SplashScreen() {
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    const [progress, setProgress] = useState(0);
    const [showContent, setShowContent] = useState(false);

    // Animate progress bar
    useEffect(() => {
        const duration = 3000; // 3 seconds
        const interval = 30; // Update every 30ms
        const increment = (100 / duration) * interval;

        const timer = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    clearInterval(timer);
                    return 100;
                }
                return prev + increment;
            });
        }, interval);

        // Show content after delay
        setTimeout(() => setShowContent(true), 500);

        return () => clearInterval(timer);
    }, []);

    // Handle navigation when progress completes - separate from state update
    useEffect(() => {
        if (progress >= 100) {
            // Mark splash as seen this session
            sessionStorage.setItem('annadata_splash_seen', 'true');

            // If already authenticated, go straight to dashboard
            if (isAuthenticated && user) {
                navigate(authService.getRedirectPath(user.role), { replace: true });
                return;
            }

            const onboardingComplete = localStorage.getItem('annadata_onboarding_complete');
            console.log('Splash Progress Complete:', { isAuthenticated, onboardingComplete });

            if (onboardingComplete === 'true') {
                navigate('/login', { replace: true });
            } else {
                navigate('/onboarding', { replace: true });
            }
        }
    }, [progress, navigate, isAuthenticated, user]);

    const handleSkip = () => {
        // Mark splash as seen this session
        sessionStorage.setItem('annadata_splash_seen', 'true');

        // If already authenticated, go straight to dashboard
        if (isAuthenticated && user) {
            navigate(authService.getRedirectPath(user.role), { replace: true });
            return;
        }

        // Respect onboarding status - if not completed, go to onboarding first
        const onboardingComplete = localStorage.getItem('annadata_onboarding_complete');
        console.log('Splash Skipped:', { isAuthenticated, onboardingComplete });

        if (onboardingComplete === 'true') {
            navigate('/login', { replace: true });
        } else {
            navigate('/onboarding', { replace: true });
        }
    };

    return (
        <div aria-label="Page Container" className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden gradient-morph"
        >
            <GEOMetadata
                pageTitle="Welcome to Annadata AI"
                description="Annadata AI is the premium smart farming platform for modern agriculture. Experience AI-powered insights, smart irrigation, and data-driven profitability."
                seoMarkers="title= name='description' og:title"
            />
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {/* Floating Circles with Glass Effect */}
                <div
                    className="absolute w-96 h-96 rounded-full bg-white/80 -top-24 -left-24"
                    style={{ animation: 'pulse 4s ease-in-out infinite' }}
                />
                <div
                    className="absolute w-64 h-64 rounded-full bg-white/80 -bottom-16 -right-16"
                    style={{ animation: 'pulse 6s ease-in-out infinite' }}
                />
                <div
                    className="absolute w-48 h-48 rounded-full bg-white/8 top-1/3 right-0"
                    style={{ animation: 'pulse 5s ease-in-out infinite' }}
                />
                {/* Additional Premium Orbs */}
                <div
                    className="absolute w-32 h-32 rounded-full top-20 left-1/4"
                    style={{
                        background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
                        animation: 'pulse 3s ease-in-out infinite',
                    }}
                />
            </div>

            {/* Logo Container with Premium Animation */}
            <div className={`
                relative z-10 text-center 
                transition-all duration-700 ease-out
                ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
            `}>
                {/* Official Logo with Breathing Glow */}
                <div className="relative w-36 h-36 mx-auto mb-8">
                    {/* Outer Glow Ring - Pulsing */}
                    <div
                        className="absolute inset-0 rounded-full logo-glow-ring"
                        style={{
                            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 60%)',
                        }}
                    />

                    {/* Inner Glow Ring */}
                    <div
                        className="absolute inset-4 rounded-full"
                        style={{
                            background: 'radial-gradient(circle, rgba(107,176,107,0.2) 0%, transparent 70%)',
                            animation: 'pulse 2s ease-in-out infinite',
                        }}
                    />

                    {/* Official Logo with Breathing Animation */}
                    <div className="absolute inset-0 flex items-center justify-center logo-reveal">
                        <Logo size={100} animated />
                    </div>
                </div>

                {/* App Name with Shimmer */}
                <h1 className="text-4xl font-bold text-gray-800 mb-2 tracking-tight">
                    ANNADATA AI
                </h1>
                <p className="text-gray-700 text-base font-hindi mb-1">
                    अन्नदाता AI
                </p>
                <p className="text-gray-500 text-sm tracking-widest uppercase">
                    Smart Farming Platform
                </p>
            </div>

            {/* Feature Highlights - Staggered */}
            <div className={`
                flex gap-8 mt-14 z-10
                transition-all duration-700 ease-out delay-500
                ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
            `}>
                {[
                    { icon: Droplets, label: 'Smart Irrigation' },
                    { icon: Sun, label: 'Weather AI' },
                    { icon: TrendingUp, label: 'Market Insights' },
                ].map((feature, i) => (
                    <div
                        key={feature.label}
                        className={`flex flex-col items-center text-gray-700 animate-stagger stagger-${i + 1}`}
                    >
                        <div className="w-12 h-12 rounded-2xl bg-white/90 backdrop-blur-sm flex items-center justify-center mb-2 border border-gray-200">
                            <feature.icon size={24} />
                        </div>
                        <span className="text-[11px] font-medium">{feature.label}</span>
                    </div>
                ))}
            </div>

            {/* Progress Bar */}
            <div className="absolute bottom-20 left-8 right-8 z-10">
                <div className="h-1 bg-white/20 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-white rounded-full transition-all duration-100"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Skip Button */}
            <button
                onClick={handleSkip}
                className={`
                    absolute bottom-8 z-10
                    text-gray-600 text-sm
                    hover:text-white transition-colors
                    touch-target
                    ${showContent ? 'opacity-100' : 'opacity-0'}
                `}
            >
                Tap to skip
            </button>

            {/* Copyright */}
            <div className="absolute bottom-4 text-center text-gray-500 text-[10px] z-10">
                <p>© 2026 Lattice Consulting Worldwide (OPC) Pvt. Ltd.</p>
            </div>
        </div>
    );
}

export default SplashScreen;
